import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { countries } from './countries';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.myeats.fr';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentView, setCurrentView] = useState('login');
  const [user, setUser] = useState(null);
  const [instances, setInstances] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [stats, setStats] = useState({ activeUsers: 0, activeInstances: 0, monthlyRevenue: 0 });
  const [allUsers, setAllUsers] = useState([]);
  const [allInstances, setAllInstances] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [adminTab, setAdminTab] = useState('dashboard');
  const [showAddCreditsModal, setShowAddCreditsModal] = useState(false);
  const [selectedUserForCredits, setSelectedUserForCredits] = useState(null);
  const [creditsAmount, setCreditsAmount] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [selectedCrypto, setSelectedCrypto] = useState(null);
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ email: '', password: '', fullName: '' });
  const [newInstance, setNewInstance] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    countryCode: '+33',
    restaurantName: '',
    restaurantAddress: '',
    city: '',
    postalCode: '',
    brandName: '',
    businessType: 'Restaurant',
    cuisineType: 'American',
    numberOfLocations: '1',
    submissionsPerDay: 1,
    totalDays: 1
  });
  // Pays sélectionné pour l'affichage
  const selectedCountry = countries.find(c => c.dialCode === newInstance.countryCode) || countries[0];

  // Filtrer les pays selon la recherche
  const filteredCountries = useMemo(() => {
    if (!countrySearch.trim()) return countries;
    const search = countrySearch.toLowerCase().trim();
    return countries.filter(c =>
      c.name.toLowerCase().includes(search) ||
      c.dialCode.includes(search) ||
      c.iso.toLowerCase().includes(search)
    );
  }, [countrySearch]);

  const handlePhoneChange = (e) => {
    const raw = e.target.value;

    // Si le numéro commence par "+", détecter automatiquement le code pays
    if (raw.startsWith('+') && raw.length >= 3) {
      const digits = raw.replace(/[^\d+]/g, ''); // Garder + et chiffres

      // Trier par longueur de dialCode décroissante pour matcher le plus long d'abord
      const sortedCountries = [...countries].sort((a, b) =>
        b.dialCode.length - a.dialCode.length
      );

      for (const country of sortedCountries) {
        if (digits.startsWith(country.dialCode)) {
          const phoneWithoutCode = digits.substring(country.dialCode.length);
          setNewInstance({
            ...newInstance,
            countryCode: country.dialCode,
            phone: phoneWithoutCode
          });
          return;
        }
      }
      // Pas de match trouvé, garder juste les chiffres après le +
      setNewInstance({...newInstance, phone: digits.substring(1)});
      return;
    }

    // Sinon, garder uniquement les chiffres (le code pays est géré par le dropdown)
    const value = raw.replace(/[^\d]/g, '');
    setNewInstance({...newInstance, phone: value});
  };

  const selectCountry = (country) => {
    setNewInstance({...newInstance, countryCode: country.dialCode});
    setCountrySearch('');
    setShowCountryDropdown(false);
  };

  // Axios instance avec token (mémorisée pour éviter la recréation)
  const api = useMemo(() => {
    const axiosInstance = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Intercepteur pour ajouter le token
    axiosInstance.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Fonction helper pour nettoyer le localStorage en cas de token invalide
    const clearAuthData = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    };

    // Intercepteur pour gérer les erreurs d'authentification
    axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 || error.response?.data?.error === 'Invalid token') {
          // Token invalide ou expiré - nettoyer le localStorage
          clearAuthData();
          // Déclencher un événement personnalisé pour notifier le composant
          window.dispatchEvent(new CustomEvent('auth-expired'));
        }
        return Promise.reject(error);
      }
    );

    return axiosInstance;
  }, []);

  // Fonctions de chargement des données (définies avant les useEffect qui les utilisent)
  const loadUserData = useCallback(async () => {
    try {
      const [instancesRes, balanceRes] = await Promise.all([
        api.get('/api/instances'),
        api.get('/api/credits/balance')
      ]);
      setInstances(instancesRes.data);
      setUser(prev => ({ ...prev, credits: balanceRes.data.credits }));
    } catch (err) {
      setError('Erreur lors du chargement des données');
    }
  }, [api]);

  const loadAdminData = useCallback(async () => {
    try {
      const [usersRes, statsRes, adminsRes] = await Promise.all([
        api.get('/api/admin/pending-users'),
        api.get('/api/admin/stats'),
        api.get('/api/admin/list-admins')
      ]);
      setPendingUsers(usersRes.data);
      setStats(statsRes.data);
      setAdmins(adminsRes.data);
    } catch (err) {
      setError('Erreur lors du chargement des données admin');
    }
  }, [api]);

  const loadAllUsers = useCallback(async () => {
    try {
      const response = await api.get('/api/admin/all-users');
      setAllUsers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Erreur chargement utilisateurs:', err.response?.status || err.message);
      }
      // Initialiser à un tableau vide en cas d'erreur
      setAllUsers([]);
      // Ne pas afficher d'erreur si c'est juste que la route n'existe pas encore
      if (err.response?.status !== 404) {
        setError('Erreur lors du chargement des utilisateurs');
      }
    }
  }, [api]);

  const loadAllInstances = useCallback(async () => {
    try {
      const response = await api.get('/api/admin/all-instances');
      setAllInstances(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Erreur chargement instances:', err.response?.status || err.message);
      }
      setAllInstances([]);
      if (err.response?.status !== 404) {
        setError('Erreur lors du chargement des instances');
      }
    }
  }, [api]);

  const loadAllPayments = useCallback(async () => {
    try {
      const response = await api.get('/api/admin/all-payments');
      setAllPayments(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Erreur chargement paiements:', err.response?.status || err.message);
      }
      setAllPayments([]);
      if (err.response?.status !== 404) {
        setError('Erreur lors du chargement des paiements');
      }
    }
  }, [api]);

  // Gestionnaire d'erreur global pour éviter les crashes
  useEffect(() => {
    const handleError = (event) => {
      // En production, ne logger que le type d'erreur sans détails sensibles
      if (process.env.NODE_ENV === 'development') {
        console.error('Erreur globale:', event.error);
      }
      setError('Une erreur inattendue s\'est produite. Veuillez réessayer.');
    };

    const handleUnhandledRejection = (event) => {
      // En production, ne logger que le type d'erreur sans détails sensibles
      if (process.env.NODE_ENV === 'development') {
        console.error('Promesse rejetée non gérée:', event.reason);
      }
      setError('Une erreur s\'est produite. Veuillez réessayer.');
      event.preventDefault(); // Empêcher le message d'erreur par défaut du navigateur
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Écouter l'événement d'expiration d'authentification
  useEffect(() => {
    const handleAuthExpired = () => {
      setIsLoggedIn(false);
      setIsAdmin(false);
      setUser(null);
      setError('Votre session a expiré. Veuillez vous reconnecter.');
      setShowCreateModal(false);
      setShowRechargeModal(false);
      setShowPaymentsModal(false);
      setShowAddCreditsModal(false);
    };

    window.addEventListener('auth-expired', handleAuthExpired);
    return () => window.removeEventListener('auth-expired', handleAuthExpired);
  }, []);

  // Vérifier si déjà connecté au chargement et gérer la vérification d'email
  useEffect(() => {
    // Vérifier les paramètres de vérification dans l'URL
    const urlParams = new URLSearchParams(window.location.search);
    const verifyStatus = urlParams.get('verify');
    const verifyMessage = urlParams.get('message');
    const verifyToken = urlParams.get('verify_token');
    const paymentStatus = urlParams.get('payment');

    // Gérer le retour de paiement
    if (paymentStatus === 'success') {
      setSuccess('Paiement réussi ! Vos crédits ont été ajoutés à votre compte.');
      loadUserData();
      // Nettoyer l'URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (paymentStatus === 'cancelled') {
      setError('Paiement annulé');
      // Nettoyer l'URL
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Si un token de vérification est présent, appeler l'API pour vérifier
    if (verifyToken) {
      const verifyEmail = async () => {
        setLoading(true);
        setError('');
        setSuccess('');
        try {
          const response = await api.get(`/api/auth/verify/${verifyToken}`);

          // Utiliser le message de la réponse API ou un message par défaut
          const successMessage = response.data?.message || 'Email vérifié avec succès ! Votre compte est en attente d\'approbation par un administrateur.';
          setSuccess(successMessage);

          // Nettoyer l'URL
          window.history.replaceState({}, '', window.location.pathname);
        } catch (err) {
          // En production, ne logger que le statut HTTP sans détails sensibles
          if (process.env.NODE_ENV === 'development') {
            console.error('Erreur de vérification:', err.response?.status || err.message);
          }

          // Utiliser le message d'erreur de l'API ou un message par défaut
          const errorMsg = err.response?.data?.message || err.response?.data?.error || 'Erreur lors de la vérification de l\'email';
          setError(errorMsg);

          // Nettoyer l'URL
          window.history.replaceState({}, '', window.location.pathname);
        } finally {
          setLoading(false);
        }
      };
      verifyEmail();
      return;
    }

    if (verifyStatus === 'success') {
      setSuccess(decodeURIComponent(verifyMessage || 'Email vérifié avec succès !'));
      // Nettoyer l'URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (verifyStatus === 'error') {
      setError(decodeURIComponent(verifyMessage || 'Erreur lors de la vérification'));
      // Nettoyer l'URL
      window.history.replaceState({}, '', window.location.pathname);
    }

    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setIsLoggedIn(true);
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      setIsAdmin(parsedUser.isAdmin);
      if (parsedUser.isAdmin) {
        loadAdminData();
      } else {
        loadUserData();
      }
    }
  }, [loadUserData, loadAdminData]);

  const loadPayments = async () => {
    try {
      const response = await api.get('/api/payments');
      setPayments(response.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Erreur chargement paiements:', err.response?.status || err.message);
      }
      setError('Erreur lors du chargement des paiements');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setShowResendVerification(false);
    try {
      const response = await api.post('/api/auth/login', loginForm);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      setIsLoggedIn(true);
      setIsAdmin(response.data.user.isAdmin);
      if (response.data.user.isAdmin) {
        loadAdminData();
      } else {
        loadUserData();
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Erreur de connexion';
      setError(errorMessage);
      // Afficher le bouton de renvoi si l'erreur est liée à la vérification d'email
      if (errorMessage === 'Please verify your email first' || errorMessage.includes('verify your email')) {
        setShowResendVerification(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!loginForm.email || !loginForm.email.trim()) {
      setError('Veuillez entrer votre email dans le champ ci-dessus');
      return;
    }

    setResendingEmail(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/api/auth/resend-verification', {
        email: loginForm.email.trim()
      });

      if (response.data.message) {
        setSuccess(response.data.message === 'Verification email sent successfully'
          ? 'Email de vérification renvoyé ! Vérifiez votre boîte de réception (et les spams).'
          : response.data.message);
      } else {
        setSuccess('Email de vérification renvoyé ! Vérifiez votre boîte de réception (et les spams).');
      }

      setShowResendVerification(false);
    } catch (err) {
      // En production, ne logger que le statut HTTP sans détails sensibles
      if (process.env.NODE_ENV === 'development') {
        console.error('Erreur lors du renvoi de l\'email:', err.response?.status || err.message);
      }

      let errorMessage = 'Erreur lors du renvoi de l\'email';

      if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        errorMessage = 'Impossible de contacter le serveur. Veuillez réessayer plus tard.';
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }

      setError(errorMessage);
    } finally {
      setResendingEmail(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await api.post('/api/auth/register', registerForm);
      setSuccess('Inscription réussie ! Vérifiez votre email.');
      setTimeout(() => setCurrentView('login'), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur d\'inscription');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    setIsAdmin(false);
    setUser(null);
    setInstances([]);
  };

  const handleCreateInstance = async () => {
    setLoading(true);
    setError('');
    try {
      await api.post('/api/instances', newInstance);
      setSuccess('Instance créée avec succès !');
      setShowCreateModal(false);
      loadUserData();
      setNewInstance({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        countryCode: '+33',
        restaurantName: '',
        restaurantAddress: '',
        city: '',
        postalCode: '',
        brandName: '',
        businessType: 'Restaurant',
        cuisineType: 'American',
        numberOfLocations: '1',
        submissionsPerDay: 1,
        totalDays: 1
      });
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erreur lors de la création';

      // Si le token est invalide, l'intercepteur gère déjà la déconnexion
      if (errorMessage === 'Invalid token' || err.response?.status === 401) {
        setError('Votre session a expiré. Veuillez vous reconnecter.');
        setShowCreateModal(false);
        // L'intercepteur déconnectera automatiquement l'utilisateur
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStopInstance = async (instanceId) => {
    if (!window.confirm('Voulez-vous vraiment arrêter cette instance ?')) return;
    try {
      const response = await api.post(`/api/instances/${instanceId}/stop`);
      setSuccess(`Instance arrêtée. Remboursement: ${response.data.refund}€`);
      loadUserData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur');
    }
  };

  const handleDeleteInstance = async (instanceId) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette instance ?')) return;
    try {
      await api.delete(`/api/instances/${instanceId}`);
      setSuccess('Instance supprimée');
      loadUserData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur');
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      await api.post(`/api/admin/approve-user/${userId}`);
      setSuccess('Utilisateur approuvé');
      loadAdminData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur');
    }
  };

  const handleRejectUser = async (userId) => {
    try {
      await api.post(`/api/admin/reject-user/${userId}`);
      setSuccess('Utilisateur rejeté');
      loadAdminData();
      loadAllUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cet utilisateur ? Cette action est irréversible.')) return;
    try {
      await api.delete(`/api/admin/users/${userId}`);
      setSuccess('Utilisateur supprimé');
      loadAllUsers();
      loadAdminData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleAddCredits = async () => {
    if (!creditsAmount || parseFloat(creditsAmount) <= 0) {
      setError('Veuillez entrer un montant valide');
      return;
    }

    if (!selectedUserForCredits || !selectedUserForCredits.id) {
      setError('Utilisateur non sélectionné');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post(`/api/admin/users/${selectedUserForCredits.id}/credits`, {
        amount: parseFloat(creditsAmount)
      });

      setSuccess(`Crédits ajoutés avec succès (${creditsAmount}€)`);

      // Attendre un peu avant de fermer le modal pour que l'utilisateur voie le message de succès
      setTimeout(async () => {
        try {
          // Recharger les données avant de fermer
          await Promise.all([loadAllUsers(), loadAdminData()]);

          // Fermer le modal seulement après le rechargement réussi
          setShowAddCreditsModal(false);
          setCreditsAmount('');
          setSelectedUserForCredits(null);
          setError('');
          setSuccess('');
        } catch (reloadErr) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Erreur rechargement données:', reloadErr.response?.status || reloadErr.message);
          }
          // Ne pas fermer le modal si le rechargement échoue
          setError('Crédits ajoutés mais erreur lors du rechargement des données. Veuillez actualiser la page.');
        }
      }, 1500);

    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Erreur ajout crédits:', err.response?.status || err.message);
      }

      // Ne pas fermer le modal en cas d'erreur
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erreur lors de l\'ajout de crédits';
      setError(errorMessage);
      setLoading(false); // Arrêter le loading pour permettre de réessayer
    }
  };

  const handleDeleteInstanceAdmin = async (instanceId) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette instance ?')) return;
    try {
      await api.delete(`/api/admin/instances/${instanceId}`);
      setSuccess('Instance supprimée');
      loadAllInstances();
      loadAdminData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur');
    }
  };

  const calculateCost = (submissionsPerDay, days) => {
    return (submissionsPerDay * days * 0.25).toFixed(2);
  };

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount('');
    setSelectedCrypto(null);
  };

  const handleCustomAmount = () => {
    const amount = parseFloat(customAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Veuillez entrer un montant valide');
      return;
    }
    setSelectedAmount(amount);
    setSelectedCrypto(null);
  };

  const handleRecharge = async () => {
    if (!selectedAmount) {
      setError('Veuillez sélectionner ou entrer un montant');
      return;
    }
    if (!selectedCrypto) {
      setError('Veuillez sélectionner une méthode de paiement');
      return;
    }

    setProcessingPayment(true);
    setError('');
    setSuccess('');
    try {
      const response = await api.post('/api/credits/create-payment', {
        amount: selectedAmount,
        pay_currency: selectedCrypto
      });

      // Vérifier plusieurs formats possibles d'URL
      const paymentUrl = response.data.payment_url || response.data.invoice_url || response.data.pay_url;

      if (paymentUrl) {
        // Rediriger vers la page de paiement NowPayments
        window.location.href = paymentUrl;
      } else if (response.data.payment_id) {
        // Si on a un payment_id mais pas d'URL, construire l'URL
        const constructedUrl = `https://nowpayments.io/payment/?iid=${response.data.payment_id}`;
        window.location.href = constructedUrl;
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.error('URL de paiement non reçue');
        }
        setError('Erreur: URL de paiement non reçue. Veuillez réessayer.');
        setProcessingPayment(false);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Erreur création paiement:', err.response?.status || err.message);
      }
      const errorMessage = err.response?.data?.error || 'Erreur lors de la création du paiement';
      setError(errorMessage);
      setProcessingPayment(false);
    }
  };

  // Fermer les messages après 5 secondes
  useEffect(() => {
    if (error) setTimeout(() => setError(''), 5000);
    if (success) setTimeout(() => setSuccess(''), 5000);
  }, [error, success]);

  // Charger les données selon l'onglet actif (admin)
  useEffect(() => {
    if (!isAdmin) return;

    // Charger les données de base admin
    loadAdminData().catch(err => {
      if (process.env.NODE_ENV === 'development') {
        console.error('Erreur chargement données admin:', err.response?.status || err.message);
      }
    });

    // Charger les données spécifiques selon l'onglet
    if (adminTab === 'users') {
      loadAllUsers().catch(err => {
        if (process.env.NODE_ENV === 'development') {
          console.error('Erreur chargement utilisateurs:', err.response?.status || err.message);
        }
      });
    } else if (adminTab === 'instances') {
      loadAllInstances().catch(err => {
        if (process.env.NODE_ENV === 'development') {
          console.error('Erreur chargement instances:', err.response?.status || err.message);
        }
      });
    } else if (adminTab === 'payments') {
      loadAllPayments().catch(err => {
        if (process.env.NODE_ENV === 'development') {
          console.error('Erreur chargement paiements:', err.response?.status || err.message);
        }
      });
    }
  }, [isAdmin, adminTab, loadAdminData, loadAllUsers, loadAllInstances, loadAllPayments]);

  // LOGIN VIEW
  if (!isLoggedIn) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom right, #16a34a, #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ background: 'white', borderRadius: '10px', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', padding: '40px', width: '100%', maxWidth: '450px' }}>
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#1f2937', marginBottom: '10px' }}>MyEats Manager</h1>
            <p style={{ color: '#6b7280' }}>Automatisez vos inscriptions UberEats</p>
          </div>

          {error && (
            <div style={{ background: '#fee2e2', border: '1px solid #ef4444', color: '#991b1b', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>
              <div style={{ marginBottom: showResendVerification ? '10px' : '0' }}>
                {error}
              </div>
              {showResendVerification && (
                <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #ef4444' }}>
                  <p style={{ fontSize: '14px', marginBottom: '10px' }}>Vous n'avez pas reçu l'email de vérification ?</p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleResendVerification(e);
                    }}
                    disabled={resendingEmail}
                    style={{
                      background: '#2563eb',
                      color: 'white',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      cursor: resendingEmail ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      opacity: resendingEmail ? 0.6 : 1,
                      width: '100%'
                    }}
                  >
                    {resendingEmail ? 'Envoi...' : 'Renvoyer l\'email de vérification'}
                  </button>
                </div>
              )}
            </div>
          )}

          {success && (
            <div style={{ background: '#d1fae5', border: '1px solid #10b981', color: '#065f46', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>
              {success}
            </div>
          )}

          {currentView === 'login' ? (
            <form onSubmit={handleLogin}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>Connexion</h2>
              <div style={{ marginBottom: '15px' }}>
                <input
                  type="email"
                  placeholder="Email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '16px' }}
                  required
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <input
                  type="password"
                  placeholder="Mot de passe"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '16px' }}
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                style={{ width: '100%', background: '#16a34a', color: 'white', padding: '12px', borderRadius: '8px', border: 'none', fontSize: '16px', fontWeight: '600', cursor: 'pointer' }}
              >
                {loading ? 'Connexion...' : 'Se connecter'}
              </button>
              <p style={{ textAlign: 'center', marginTop: '20px', color: '#6b7280', fontSize: '14px' }}>
                Pas de compte ?{' '}
                <button
                  type="button"
                  onClick={() => setCurrentView('register')}
                  style={{ color: '#16a34a', background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
                >
                  S'inscrire
                </button>
              </p>
            </form>
          ) : (
            <form onSubmit={handleRegister}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>Inscription</h2>
              <div style={{ marginBottom: '15px' }}>
                <input
                  type="text"
                  placeholder="Nom complet"
                  value={registerForm.fullName}
                  onChange={(e) => setRegisterForm({ ...registerForm, fullName: e.target.value })}
                  style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '16px' }}
                  required
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <input
                  type="email"
                  placeholder="Email"
                  value={registerForm.email}
                  onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                  style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '16px' }}
                  required
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <input
                  type="password"
                  placeholder="Mot de passe"
                  value={registerForm.password}
                  onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                  style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '16px' }}
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                style={{ width: '100%', background: '#16a34a', color: 'white', padding: '12px', borderRadius: '8px', border: 'none', fontSize: '16px', fontWeight: '600', cursor: 'pointer' }}
              >
                {loading ? 'Inscription...' : 'S\'inscrire'}
              </button>
              <p style={{ textAlign: 'center', marginTop: '20px', color: '#6b7280', fontSize: '14px' }}>
                Déjà un compte ?{' '}
                <button
                  type="button"
                  onClick={() => setCurrentView('login')}
                  style={{ color: '#16a34a', background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
                >
                  Se connecter
                </button>
              </p>
            </form>
          )}
        </div>
      </div>
    );
  }

  // ADMIN PANEL
  if (isAdmin) {
    const tabs = [
      { id: 'dashboard', label: '📊 Dashboard' },
      { id: 'users', label: '👥 Utilisateurs' },
      { id: 'instances', label: '🔄 Instances' },
      { id: 'payments', label: '💳 Paiements' },
      { id: 'pending', label: '⏳ En attente' }
    ];

    return (
      <div style={{ minHeight: '100vh', background: '#f3f4f6' }}>
        <nav style={{ background: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>MyEats Admin Panel</h1>
            <button
              onClick={handleLogout}
              style={{ background: '#ef4444', color: 'white', padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}
            >
              Déconnexion
            </button>
          </div>
        </nav>

        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '30px' }}>
          {error && (
            <div style={{ background: '#fee2e2', border: '1px solid #ef4444', color: '#991b1b', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>
              {error}
            </div>
          )}

          {success && (
            <div style={{ background: '#d1fae5', border: '1px solid #10b981', color: '#065f46', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>
              {success}
            </div>
          )}

          {/* Onglets */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '30px', borderBottom: '2px solid #e5e7eb' }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setAdminTab(tab.id)}
                style={{
                  padding: '12px 24px',
                  background: adminTab === tab.id ? '#2563eb' : 'transparent',
                  color: adminTab === tab.id ? 'white' : '#6b7280',
                  border: 'none',
                  borderBottom: adminTab === tab.id ? '2px solid #2563eb' : '2px solid transparent',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: adminTab === tab.id ? '600' : '400',
                  marginBottom: '-2px'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Contenu des onglets */}
          {adminTab === 'dashboard' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                <div style={{ background: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                  <p style={{ color: '#6b7280', fontSize: '14px' }}>Utilisateurs actifs</p>
                  <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#2563eb' }}>{stats.activeUsers}</p>
                </div>
                <div style={{ background: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                  <p style={{ color: '#6b7280', fontSize: '14px' }}>Instances actives</p>
                  <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#16a34a' }}>{stats.activeInstances}</p>
                </div>
                <div style={{ background: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                  <p style={{ color: '#6b7280', fontSize: '14px' }}>Revenus du mois</p>
                  <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#16a34a' }}>€{stats.monthlyRevenue != null ? parseFloat(stats.monthlyRevenue).toFixed(2) : '0.00'}</p>
                </div>
              </div>

              <div style={{ background: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>Administrateurs</h2>
                {admins.length === 0 ? (
                  <p style={{ color: '#6b7280', textAlign: 'center', padding: '40px' }}>Aucun administrateur</p>
                ) : (
                  <div>
                    {admins.map(admin => (
                      <div key={admin.id} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '15px', marginBottom: '15px' }}>
                        <p style={{ fontWeight: '600', marginBottom: '5px' }}>{admin.email}</p>
                        <p style={{ fontSize: '14px', color: '#6b7280' }}>{admin.full_name || 'Administrateur'} - Créé le {new Date(admin.created_at).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {adminTab === 'users' && (
            <div style={{ background: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600' }}>Tous les utilisateurs ({allUsers?.length || 0})</h2>
                <button
                  onClick={loadAllUsers}
                  style={{ background: '#2563eb', color: 'white', padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                >
                  🔄 Actualiser
                </button>
              </div>
              {!allUsers || allUsers.length === 0 ? (
                <p style={{ color: '#6b7280', textAlign: 'center', padding: '40px' }}>Aucun utilisateur</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>ID</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Email</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Nom</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Statut</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Crédits</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Créé le</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allUsers.map(user => (
                        <tr key={user.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '12px' }}>{user.id}</td>
                          <td style={{ padding: '12px', fontWeight: '500' }}>{user.email}</td>
                          <td style={{ padding: '12px' }}>{user.full_name || '-'}</td>
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '600',
                              background: user.account_status === 'approved' ? '#d1fae5' : user.account_status === 'pending' ? '#fef3c7' : '#fee2e2',
                              color: user.account_status === 'approved' ? '#065f46' : user.account_status === 'pending' ? '#92400e' : '#991b1b'
                            }}>
                              {user.account_status === 'approved' ? 'Approuvé' : user.account_status === 'pending' ? 'En attente' : 'Rejeté'}
                            </span>
                          </td>
                          <td style={{ padding: '12px', fontWeight: '600', color: '#16a34a' }}>
                            {(user.credits != null ? parseFloat(user.credits).toFixed(2) : '0.00')}€
                          </td>
                          <td style={{ padding: '12px', color: '#6b7280', fontSize: '14px' }}>
                            {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : '-'}
                          </td>
                          <td style={{ padding: '12px' }}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => {
                                  setSelectedUserForCredits(user);
                                  setShowAddCreditsModal(true);
                                }}
                                style={{ background: '#16a34a', color: 'white', padding: '6px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '12px' }}
                              >
                                + Crédits
                              </button>
                              {user.account_status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => handleApproveUser(user.id)}
                                    style={{ background: '#2563eb', color: 'white', padding: '6px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '12px' }}
                                  >
                                    ✓
                                  </button>
                                  <button
                                    onClick={() => handleRejectUser(user.id)}
                                    style={{ background: '#f59e0b', color: 'white', padding: '6px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '12px' }}
                                  >
                                    ✗
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => handleDeleteUser(user.id)}
                                style={{ background: '#ef4444', color: 'white', padding: '6px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '12px' }}
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {adminTab === 'instances' && (
            <div style={{ background: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600' }}>Toutes les instances ({allInstances.length})</h2>
                <button
                  onClick={loadAllInstances}
                  style={{ background: '#2563eb', color: 'white', padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                >
                  🔄 Actualiser
                </button>
              </div>
              {allInstances.length === 0 ? (
                <p style={{ color: '#6b7280', textAlign: 'center', padding: '40px' }}>Aucune instance</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>ID</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Restaurant</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Email</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Statut</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Progression</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Jours restants</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Coût</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Créé le</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allInstances.map(instance => {
                        const progress = instance.total_submissions > 0 ? (instance.submissions_done / instance.total_submissions * 100).toFixed(0) : 0;
                        return (
                          <tr key={instance.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '12px' }}>{instance.id}</td>
                            <td style={{ padding: '12px', fontWeight: '500' }}>{instance.restaurant_name}</td>
                            <td style={{ padding: '12px' }}>{instance.email}</td>
                            <td style={{ padding: '12px' }}>
                              <span style={{
                                padding: '4px 12px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                background: instance.status === 'active' ? '#d1fae5' : instance.status === 'completed' ? '#dbeafe' : '#fee2e2',
                                color: instance.status === 'active' ? '#065f46' : instance.status === 'completed' ? '#1e40af' : '#991b1b'
                              }}>
                                {instance.status === 'active' ? 'Active' : instance.status === 'completed' ? 'Terminée' : 'Arrêtée'}
                              </span>
                            </td>
                            <td style={{ padding: '12px' }}>
                              {instance.submissions_done}/{instance.total_submissions} ({progress}%)
                            </td>
                            <td style={{ padding: '12px' }}>{instance.days_remaining}/{instance.total_days}</td>
                            <td style={{ padding: '12px', fontWeight: '600', color: '#92400e' }}>{instance.cost}€</td>
                            <td style={{ padding: '12px', color: '#6b7280', fontSize: '14px' }}>
                              {new Date(instance.created_at).toLocaleDateString('fr-FR')}
                            </td>
                            <td style={{ padding: '12px' }}>
                              <button
                                onClick={() => handleDeleteInstanceAdmin(instance.id)}
                                style={{ background: '#ef4444', color: 'white', padding: '6px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '12px' }}
                              >
                                🗑️ Supprimer
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {adminTab === 'payments' && (
            <div style={{ background: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600' }}>Tous les paiements ({allPayments.length})</h2>
                <button
                  onClick={loadAllPayments}
                  style={{ background: '#2563eb', color: 'white', padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                >
                  🔄 Actualiser
                </button>
              </div>
              {allPayments.length === 0 ? (
                <p style={{ color: '#6b7280', textAlign: 'center', padding: '40px' }}>Aucun paiement</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>ID</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Utilisateur</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Montant</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Méthode</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Statut</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Date</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Payment ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allPayments.map(payment => {
                        const getStatusColor = (status) => {
                          switch (status) {
                            case 'finished':
                            case 'confirmed':
                              return { bg: '#d1fae5', color: '#065f46', text: 'Confirmé' };
                            case 'waiting':
                            case 'confirming':
                              return { bg: '#fef3c7', color: '#92400e', text: 'En attente' };
                            case 'sending':
                            case 'partially_paid':
                              return { bg: '#dbeafe', color: '#1e40af', text: 'En cours' };
                            case 'failed':
                            case 'expired':
                              return { bg: '#fee2e2', color: '#991b1b', text: 'Échoué' };
                            default:
                              return { bg: '#f3f4f6', color: '#6b7280', text: status };
                          }
                        };
                        const statusStyle = getStatusColor(payment.status);
                        const cryptoName = payment.pay_currency === 'btc' ? 'Bitcoin' :
                                         payment.pay_currency === 'eth' ? 'Ethereum' :
                                         payment.pay_currency === 'ltc' ? 'Litecoin' :
                                         payment.pay_currency === 'usdterc20' ? 'USDT' :
                                         payment.pay_currency || 'N/A';
                        return (
                          <tr key={payment.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '12px' }}>{payment.id}</td>
                            <td style={{ padding: '12px' }}>{payment.user_email || `User #${payment.user_id}`}</td>
                            <td style={{ padding: '12px', fontWeight: '600', color: '#16a34a' }}>{payment.amount}€</td>
                            <td style={{ padding: '12px' }}>{cryptoName}</td>
                            <td style={{ padding: '12px' }}>
                              <span style={{
                                padding: '4px 12px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                background: statusStyle.bg,
                                color: statusStyle.color
                              }}>
                                {statusStyle.text}
                              </span>
                            </td>
                            <td style={{ padding: '12px', color: '#6b7280', fontSize: '14px' }}>
                              {new Date(payment.created_at).toLocaleString('fr-FR')}
                            </td>
                            <td style={{ padding: '12px', fontSize: '12px', color: '#9ca3af', fontFamily: 'monospace' }}>
                              {payment.payment_id || '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {adminTab === 'pending' && (
            <div style={{ background: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>Utilisateurs en attente d'approbation</h2>
              {pendingUsers.length === 0 ? (
                <p style={{ color: '#6b7280', textAlign: 'center', padding: '40px' }}>Aucun utilisateur en attente</p>
              ) : (
                <div>
                  {pendingUsers.map(user => (
                    <div key={user.id} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '15px', marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <p style={{ fontWeight: '600' }}>{user.email}</p>
                        <p style={{ fontSize: '14px', color: '#6b7280' }}>{user.full_name || 'Utilisateur'} - Inscrit le {new Date(user.created_at).toLocaleDateString('fr-FR')}</p>
                      </div>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                          onClick={() => handleApproveUser(user.id)}
                          style={{ background: '#16a34a', color: 'white', padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                        >
                          Approuver
                        </button>
                        <button
                          onClick={() => handleRejectUser(user.id)}
                          style={{ background: '#ef4444', color: 'white', padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                        >
                          Rejeter
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Ajout de crédits */}
        {showAddCreditsModal && selectedUserForCredits && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 50 }}>
            <div style={{ background: 'white', borderRadius: '10px', padding: '30px', width: '100%', maxWidth: '500px' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '20px' }}>Ajouter des crédits</h2>

              {error && (
                <div style={{ background: '#fee2e2', border: '1px solid #ef4444', color: '#991b1b', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>
                  {error}
                </div>
              )}

              {success && (
                <div style={{ background: '#d1fae5', border: '1px solid #10b981', color: '#065f46', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>
                  {success}
                </div>
              )}

              <div style={{ marginBottom: '20px' }}>
                <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Utilisateur:</p>
                <p style={{ fontSize: '18px', fontWeight: '600' }}>{selectedUserForCredits.email}</p>
                <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                  Crédits actuels: <span style={{ fontWeight: '600', color: '#16a34a' }}>
                    {selectedUserForCredits.credits != null ? parseFloat(selectedUserForCredits.credits).toFixed(2) : '0.00'}€
                  </span>
                </p>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  Montant à ajouter (€)
                </label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={creditsAmount}
                  onChange={(e) => setCreditsAmount(e.target.value)}
                  placeholder="Ex: 50.00"
                  style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '16px' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAddCredits();
                  }}
                  disabled={loading || !creditsAmount || parseFloat(creditsAmount) <= 0}
                  style={{
                    flex: 1,
                    background: loading || !creditsAmount || parseFloat(creditsAmount) <= 0 ? '#9ca3af' : '#16a34a',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: loading || !creditsAmount || parseFloat(creditsAmount) <= 0 ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    opacity: loading ? 0.6 : 1
                  }}
                >
                  {loading ? 'Ajout...' : `Ajouter ${creditsAmount ? `${creditsAmount}€` : ''}`}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowAddCreditsModal(false);
                    setSelectedUserForCredits(null);
                    setCreditsAmount('');
                    setError('');
                    setSuccess('');
                  }}
                  disabled={loading}
                  style={{
                    padding: '12px 24px',
                    background: '#e5e7eb',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1
                  }}
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // USER PANEL
  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6' }}>
      <nav style={{ background: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>MyEats Manager</h1>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <div style={{ background: '#d1fae5', padding: '10px 20px', borderRadius: '8px', fontWeight: '600', color: '#065f46' }}>
              {user?.credits != null ? parseFloat(user.credits).toFixed(2) : '0.00'}€
            </div>
            <button
              onClick={() => {
                setShowPaymentsModal(true);
                loadPayments();
              }}
              style={{ background: '#6b7280', color: 'white', padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}
            >
              Historique
            </button>
            <button
              onClick={() => setShowRechargeModal(true)}
              style={{ background: '#2563eb', color: 'white', padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}
            >
              Recharger
            </button>
            <button
              onClick={handleLogout}
              style={{ background: '#ef4444', color: 'white', padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}
            >
              Déconnexion
            </button>
          </div>
        </div>
      </nav>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '30px' }}>
        {error && (
          <div style={{ background: '#fee2e2', border: '1px solid #ef4444', color: '#991b1b', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ background: '#d1fae5', border: '1px solid #10b981', color: '#065f46', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>
            {success}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold' }}>Mes Instances</h2>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{ background: '#16a34a', color: 'white', padding: '12px 24px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600' }}
          >
            + Nouvelle Instance
          </button>
        </div>

        {instances.length === 0 ? (
          <div style={{ background: 'white', borderRadius: '10px', padding: '60px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <p style={{ color: '#6b7280', fontSize: '18px' }}>Aucune instance créée pour le moment</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '20px' }}>
            {instances.map(instance => {
              const progress = (instance.submissions_done / instance.total_submissions) * 100;
              return (
                <div key={instance.id} style={{ background: 'white', borderRadius: '10px', padding: '25px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                    <div>
                      <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '5px' }}>{instance.restaurant_name}</h3>
                      <p style={{ color: '#6b7280' }}>{instance.email}</p>
                    </div>
                    <span style={{
                      padding: '5px 15px',
                      borderRadius: '20px',
                      fontSize: '14px',
                      fontWeight: '600',
                      background: instance.status === 'active' ? '#d1fae5' : '#fee2e2',
                      color: instance.status === 'active' ? '#065f46' : '#991b1b'
                    }}>
                      {instance.status === 'active' ? 'Active' : instance.status === 'completed' ? 'Terminée' : 'Arrêtée'}
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                    <div style={{ background: '#dbeafe', padding: '15px', borderRadius: '8px' }}>
                      <p style={{ fontSize: '12px', color: '#1e40af' }}>Soumissions/jour</p>
                      <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e40af' }}>{instance.submissions_per_day}</p>
                    </div>
                    <div style={{ background: '#e0e7ff', padding: '15px', borderRadius: '8px' }}>
                      <p style={{ fontSize: '12px', color: '#4338ca' }}>Jours restants</p>
                      <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#4338ca' }}>{instance.days_remaining}/{instance.total_days}</p>
                    </div>
                    <div style={{ background: '#d1fae5', padding: '15px', borderRadius: '8px' }}>
                      <p style={{ fontSize: '12px', color: '#065f46' }}>Progression</p>
                      <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#065f46' }}>{instance.submissions_done}/{instance.total_submissions}</p>
                    </div>
                    <div style={{ background: '#fed7aa', padding: '15px', borderRadius: '8px' }}>
                      <p style={{ fontSize: '12px', color: '#92400e' }}>Coût total</p>
                      <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#92400e' }}>{instance.cost}€</p>
                    </div>
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                      <span>Progression</span>
                      <span>{progress.toFixed(0)}%</span>
                    </div>
                    <div style={{ width: '100%', background: '#e5e7eb', borderRadius: '10px', height: '10px', overflow: 'hidden' }}>
                      <div style={{ width: `${progress}%`, background: '#16a34a', height: '100%', transition: 'width 0.3s' }} />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    {instance.status === 'active' && (
                      <button
                        onClick={() => handleStopInstance(instance.id)}
                        style={{ background: '#f59e0b', color: 'white', padding: '10px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                      >
                        Arrêter (remboursement)
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteInstance(instance.id)}
                      style={{ background: '#ef4444', color: 'white', padding: '10px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal Création Instance */}
      {showCreateModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 50 }}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '30px', width: '100%', maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '20px' }}>Créer une nouvelle instance</h2>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '20px' }}>
              <input
                type="text"
                placeholder="Prénom"
                value={newInstance.firstName}
                onChange={(e) => setNewInstance({...newInstance, firstName: e.target.value})}
                style={{ padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px' }}
              />
              <input
                type="text"
                placeholder="Nom"
                value={newInstance.lastName}
                onChange={(e) => setNewInstance({...newInstance, lastName: e.target.value})}
                style={{ padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px' }}
              />
              <input
                type="email"
                placeholder="Email"
                value={newInstance.email}
                onChange={(e) => setNewInstance({...newInstance, email: e.target.value})}
                style={{ padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px' }}
              />
              {/* Code pays + Téléphone */}
              <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '10px' }}>
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCountryDropdown(!showCountryDropdown);
                      setCountrySearch('');
                    }}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      background: 'white',
                      cursor: 'pointer',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '4px'
                    }}
                  >
                    <span>{selectedCountry.flag} {selectedCountry.dialCode}</span>
                    <span style={{ fontSize: '10px', color: '#9ca3af' }}>▼</span>
                  </button>

                  {showCountryDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      width: '300px',
                      background: 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                      zIndex: 100,
                      maxHeight: '280px',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <div style={{ padding: '8px', borderBottom: '1px solid #e5e7eb' }}>
                        <input
                          type="text"
                          placeholder="Rechercher pays ou code..."
                          value={countrySearch}
                          onChange={(e) => setCountrySearch(e.target.value)}
                          autoFocus
                          style={{
                            width: '100%',
                            padding: '8px 10px',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontSize: '14px',
                            outline: 'none'
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              setShowCountryDropdown(false);
                              setCountrySearch('');
                            }
                            if (e.key === 'Enter' && filteredCountries.length > 0) {
                              selectCountry(filteredCountries[0]);
                            }
                          }}
                        />
                      </div>
                      <div style={{ overflowY: 'auto', maxHeight: '220px' }}>
                        {filteredCountries.length === 0 ? (
                          <div style={{ padding: '12px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
                            Aucun résultat
                          </div>
                        ) : (
                          filteredCountries.map(country => (
                            <button
                              key={country.iso}
                              type="button"
                              onClick={() => selectCountry(country)}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: 'none',
                                background: country.dialCode === newInstance.countryCode ? '#dbeafe' : 'transparent',
                                cursor: 'pointer',
                                textAlign: 'left',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}
                              onMouseEnter={(e) => {
                                if (country.dialCode !== newInstance.countryCode) {
                                  e.currentTarget.style.background = '#f3f4f6';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = country.dialCode === newInstance.countryCode ? '#dbeafe' : 'transparent';
                              }}
                            >
                              <span>{country.flag}</span>
                              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{country.name}</span>
                              <span style={{ color: '#6b7280', fontSize: '13px', flexShrink: 0 }}>{country.dialCode}</span>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              <input
                type="tel"
                placeholder="( 612345678.)"
                value={newInstance.phone}
                onChange={handlePhoneChange}
                onFocus={() => setShowCountryDropdown(false)}
                style={{ padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px' }}
              />
              </div>
              <input
                type="text"
                placeholder="Nom du restaurant"
                value={newInstance.restaurantName}
                onChange={(e) => {
                  const restaurantName = e.target.value;
                  setNewInstance({
                    ...newInstance,
                    restaurantName: restaurantName,
                    brandName: newInstance.brandName || restaurantName
                  });
                }}
                style={{ padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', gridColumn: 'span 2' }}
              />
              <input
                type="text"
                placeholder="Adresse du restaurant"
                value={newInstance.restaurantAddress}
                onChange={(e) => {
                  const address = e.target.value;
                  // Extraire la ville et le code postal de l'adresse
                  let city = '';
                  let postalCode = '';

                  const addressParts = address.split(',').map(part => part.trim());
                  if (addressParts.length >= 2) {
                    city = addressParts[1];
                  } else if (addressParts.length === 1) {
                    const words = addressParts[0].split(' ');
                    if (words.length > 1) {
                      city = words[words.length - 1];
                    } else {
                      city = addressParts[0];
                    }
                  }

                  const postalCodeMatch = address.match(/\b\d{5}\b/);
                  if (postalCodeMatch) {
                    postalCode = postalCodeMatch[0];
                  } else {
                    const altMatch = address.match(/\b\d{4,6}\b/);
                    if (altMatch) {
                      postalCode = altMatch[0];
                    }
                  }

                  if (!city && addressParts.length >= 2) {
                    city = addressParts[1];
                  }

                  setNewInstance({
                    ...newInstance,
                    restaurantAddress: address,
                    city: city || newInstance.city,
                    postalCode: postalCode || newInstance.postalCode
                  });
                }}
                style={{ padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', gridColumn: 'span 2' }}
              />
              <input
                type="text"
                placeholder="Ville"
                value={newInstance.city}
                onChange={(e) => setNewInstance({...newInstance, city: e.target.value})}
                style={{ padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px' }}
              />
              <input
                type="text"
                placeholder="Code postal"
                value={newInstance.postalCode}
                onChange={(e) => setNewInstance({...newInstance, postalCode: e.target.value})}
                style={{ padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px' }}
              />
              <input
                type="text"
                placeholder="Nom de marque (Brand name)"
                value={newInstance.brandName}
                onChange={(e) => setNewInstance({...newInstance, brandName: e.target.value})}
                style={{ padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', gridColumn: 'span 2' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', gridColumn: 'span 2' }}>
                <label style={{ fontSize: '14px', color: '#6b7280', whiteSpace: 'nowrap', minWidth: '140px' }}>Type d'entreprise</label>
                <select
                  value={newInstance.businessType}
                  onChange={(e) => {
                    const newBusinessType = e.target.value;
                    // Si on change pour un type autre que Restaurant, réinitialiser cuisineType et numberOfLocations
                    const updates = { businessType: newBusinessType };
                    if (newBusinessType !== 'Restaurant') {
                      updates.cuisineType = 'American'; // Valeur par défaut mais ne sera pas utilisé
                      updates.numberOfLocations = '1'; // Valeur par défaut mais ne sera pas utilisé
                    }
                    setNewInstance({...newInstance, ...updates});
                  }}
                  style={{ padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', flex: 1 }}
                >
                  <option value="Restaurant">Restaurant</option>
                  <option value="Convenience store">Convenience store</option>
                  <option value="Grocery store">Grocery store</option>
                  <option value="Specialty food store">Specialty food store</option>
                  <option value="Liquor store">Liquor store</option>
                  <option value="Florist">Florist</option>
                  <option value="Pharmacy">Pharmacy</option>
                  <option value="Retail">Retail</option>
                </select>
              </div>
              {newInstance.businessType === 'Restaurant' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', gridColumn: 'span 2' }}>
                  <label style={{ fontSize: '14px', color: '#6b7280', whiteSpace: 'nowrap', minWidth: '140px' }}>Type de cuisine</label>
                  <select
                    value={newInstance.cuisineType}
                    onChange={(e) => setNewInstance({...newInstance, cuisineType: e.target.value})}
                    style={{ padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', flex: 1 }}
                  >
                  <option value="African: Ethiopian">African: Ethiopian</option>
                  <option value="African: Other">African: Other</option>
                  <option value="Alcohol">Alcohol</option>
                  <option value="American">American</option>
                  <option value="Argentinian">Argentinian</option>
                  <option value="Asian">Asian</option>
                  <option value="Asian: Other">Asian: Other</option>
                  <option value="Asian Fusion">Asian Fusion</option>
                  <option value="Bakery">Bakery</option>
                  <option value="Bakery & Cake Shop">Bakery & Cake Shop</option>
                  <option value="Bangladeshi">Bangladeshi</option>
                  <option value="Bar / Pub Food">Bar / Pub Food</option>
                  <option value="BBQ">BBQ</option>
                  <option value="Brazilian">Brazilian</option>
                  <option value="Breakfast & Brunch">Breakfast & Brunch</option>
                  <option value="Bubble Tea">Bubble Tea</option>
                  <option value="Burgers">Burgers</option>
                  <option value="Burmese">Burmese</option>
                  <option value="Burrito">Burrito</option>
                  <option value="Cajun / Creole">Cajun / Creole</option>
                  <option value="Cake">Cake</option>
                  <option value="Caribbean">Caribbean</option>
                  <option value="Chicken">Chicken</option>
                  <option value="Chilean">Chilean</option>
                  <option value="Chinese: Cantonese">Chinese: Cantonese</option>
                  <option value="Chinese: Hot Pot">Chinese: Hot Pot</option>
                  <option value="Chinese: Noodles & Dumplings">Chinese: Noodles & Dumplings</option>
                  <option value="Chinese: Other">Chinese: Other</option>
                  <option value="Chinese: Sichuan">Chinese: Sichuan</option>
                  <option value="Chinese: Taiwanese">Chinese: Taiwanese</option>
                  <option value="Coffee & Tea">Coffee & Tea</option>
                  <option value="Colombian">Colombian</option>
                  <option value="Crepe or Creperie">Crepe or Creperie</option>
                  <option value="Comfort Food">Comfort Food</option>
                  <option value="Dessert">Dessert</option>
                  <option value="Dessert: Other">Dessert: Other</option>
                  <option value="Ecuadorian">Ecuadorian</option>
                  <option value="Egyptian">Egyptian</option>
                  <option value="Empanada">Empanada</option>
                  <option value="European">European</option>
                  <option value="European: Other">European: Other</option>
                  <option value="Filipino">Filipino</option>
                  <option value="Fish and Chips">Fish and Chips</option>
                  <option value="Fish & Seafood">Fish & Seafood</option>
                  <option value="French">French</option>
                  <option value="Georgian">Georgian</option>
                  <option value="German">German</option>
                  <option value="Gourmet">Gourmet</option>
                  <option value="Greek">Greek</option>
                  <option value="Guatemalan">Guatemalan</option>
                  <option value="Halal">Halal</option>
                  <option value="Hawaiian">Hawaiian</option>
                  <option value="Healthy">Healthy</option>
                  <option value="Ice Cream & Frozen Yogurt">Ice Cream & Frozen Yogurt</option>
                  <option value="Indian">Indian</option>
                  <option value="Indonesian">Indonesian</option>
                  <option value="Israeli">Israeli</option>
                  <option value="Italian">Italian</option>
                  <option value="Japanese: Other">Japanese: Other</option>
                  <option value="Japanese: Ramen">Japanese: Ramen</option>
                  <option value="Japanese: Sushi">Japanese: Sushi</option>
                  <option value="Juice & Smoothies">Juice & Smoothies</option>
                  <option value="Kebab">Kebab</option>
                  <option value="Korean">Korean</option>
                  <option value="Kosher">Kosher</option>
                  <option value="Latin American: Other">Latin American: Other</option>
                  <option value="Lebanese">Lebanese</option>
                  <option value="Malaysian">Malaysian</option>
                  <option value="Mediterranean">Mediterranean</option>
                  <option value="Mexican">Mexican</option>
                  <option value="Middle Eastern">Middle Eastern</option>
                  <option value="Modern Australian">Modern Australian</option>
                  <option value="Moroccan">Moroccan</option>
                  <option value="Pakistani">Pakistani</option>
                  <option value="Peruvian">Peruvian</option>
                  <option value="Pizza">Pizza</option>
                  <option value="Poke">Poke</option>
                  <option value="Portuguese">Portuguese</option>
                  <option value="Russian">Russian</option>
                  <option value="Salad / Sandwiches">Salad / Sandwiches</option>
                  <option value="Seafood">Seafood</option>
                  <option value="Snacks">Snacks</option>
                  <option value="Soul Food">Soul Food</option>
                  <option value="Southern">Southern</option>
                  <option value="Spanish">Spanish</option>
                  <option value="Steakhouse">Steakhouse</option>
                  <option value="Sushi">Sushi</option>
                  <option value="Tacos">Tacos</option>
                  <option value="Tex-Mex">Tex-Mex</option>
                  <option value="Thai">Thai</option>
                  <option value="Turkish">Turkish</option>
                  <option value="Vegetarian / Vegan">Vegetarian / Vegan</option>
                  <option value="Venezuelan">Venezuelan</option>
                  <option value="Vietnamese">Vietnamese</option>
                  <option value="Wings">Wings</option>
                  <option value="Other">Other</option>
                  </select>
                </div>
              )}
              {newInstance.businessType === 'Restaurant' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', gridColumn: 'span 2' }}>
                  <label style={{ fontSize: '14px', color: '#6b7280', whiteSpace: 'nowrap', minWidth: '140px' }}>Nombre de locations</label>
                  <select
                    value={newInstance.numberOfLocations}
                    onChange={(e) => setNewInstance({...newInstance, numberOfLocations: e.target.value})}
                    style={{ padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', flex: 1 }}
                  >
                    <option value="1">1</option>
                    <option value="2-5">2-5</option>
                    <option value="6-10">6-10</option>
                    <option value="11-25">11-25</option>
                    <option value="25+">25+</option>
                  </select>
                </div>
              )}
              {newInstance.businessType !== 'Restaurant' && (
                <div style={{ gridColumn: 'span 2', padding: '10px', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '6px', fontSize: '14px', color: '#92400e' }}>
                  ℹ️ Les champs "Type de cuisine" et "Nombre de locations" ne sont disponibles que pour les restaurants.
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{ fontSize: '14px', color: '#6b7280', whiteSpace: 'nowrap', minWidth: '140px' }}>Soumissions par jour</label>
                <input
                  type="number"
                  min="1"
                  value={newInstance.submissionsPerDay}
                  onChange={(e) => setNewInstance({...newInstance, submissionsPerDay: parseInt(e.target.value) || 1})}
                  style={{ padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', flex: 1 }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{ fontSize: '14px', color: '#6b7280', whiteSpace: 'nowrap', minWidth: '140px' }}>Nombre de jours</label>
                <input
                  type="number"
                  min="1"
                  value={newInstance.totalDays}
                  onChange={(e) => setNewInstance({...newInstance, totalDays: parseInt(e.target.value) || 1})}
                  style={{ padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', flex: 1 }}
                />
              </div>
            </div>

            <div style={{ background: '#dbeafe', border: '1px solid #3b82f6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{ fontWeight: '600' }}>Soumissions totales:</span>
                <span style={{ fontSize: '18px', fontWeight: 'bold' }}>{newInstance.submissionsPerDay * newInstance.totalDays}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: '600' }}>Coût total:</span>
                <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#16a34a' }}>
                  {calculateCost(newInstance.submissionsPerDay, newInstance.totalDays)}€
                </span>
              </div>
              <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '10px' }}>
                (0.25€ par soumission)
              </p>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleCreateInstance}
                disabled={loading}
                style={{ flex: 1, background: '#16a34a', color: 'white', padding: '12px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600' }}
              >
                {loading ? 'Création...' : `Créer et payer ${calculateCost(newInstance.submissionsPerDay, newInstance.totalDays)}€`}
              </button>
              <button
  onClick={() => {
    setShowCreateModal(false);
    setNewInstance({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      countryCode: '+33',
      restaurantName: '',
      restaurantAddress: '',
      city: '',
      postalCode: '',
      brandName: '',
      businessType: 'Restaurant',
      cuisineType: 'American',
      numberOfLocations: '1',
      submissionsPerDay: 1,
      totalDays: 1
    });
  }}
  style={{ padding: '12px 24px', background: '#e5e7eb', borderRadius: '8px', border: 'none', cursor: 'pointer' }}
>
  Annuler
</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Recharge */}
      {showRechargeModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 50 }}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '30px', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '20px' }}>Recharger mon compte</h2>

            {!selectedAmount ? (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '15px', color: '#1f2937' }}>Montants prédéfinis</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '20px' }}>
                    <button
                      onClick={() => handleAmountSelect(10)}
                      style={{
                        padding: '15px',
                        background: '#16a34a',
                        color: 'white',
                        borderRadius: '8px',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '600'
                      }}
                    >
                      Recharger 10€
                    </button>
                    <button
                      onClick={() => handleAmountSelect(25)}
                      style={{
                        padding: '15px',
                        background: '#16a34a',
                        color: 'white',
                        borderRadius: '8px',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '600'
                      }}
                    >
                      Recharger 25€
                    </button>
                    <button
                      onClick={() => handleAmountSelect(50)}
                      style={{
                        padding: '15px',
                        background: '#16a34a',
                        color: 'white',
                        borderRadius: '8px',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '600'
                      }}
                    >
                      Recharger 50€
                    </button>
                    <button
                      onClick={() => handleAmountSelect(100)}
                      style={{
                        padding: '15px',
                        background: '#16a34a',
                        color: 'white',
                        borderRadius: '8px',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '600'
                      }}
                    >
                      Recharger 100€
                    </button>
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '10px', color: '#1f2937' }}>Ou montant personnalisé</p>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                      type="number"
                      min="1"
                      step="0.01"
                      placeholder="Montant en €"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleCustomAmount()}
                      style={{
                        flex: 1,
                        padding: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '16px'
                      }}
                    />
                    <button
                      onClick={handleCustomAmount}
                      style={{
                        padding: '12px 24px',
                        background: '#2563eb',
                        color: 'white',
                        borderRadius: '8px',
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      Valider
                    </button>
                  </div>
                </div>
              </>
            ) : !selectedCrypto ? (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <p style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                      Montant sélectionné: <span style={{ color: '#16a34a' }}>{selectedAmount}€</span>
                    </p>
                    <button
                      onClick={() => {
                        setSelectedAmount(null);
                        setCustomAmount('');
                        setSelectedCrypto(null);
                      }}
                      style={{
                        padding: '8px 16px',
                        background: '#e5e7eb',
                        color: '#6b7280',
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Changer
                    </button>
                  </div>

                  <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '15px', color: '#1f2937' }}>Choisissez votre méthode de paiement</p>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    <button
                      onClick={() => setSelectedCrypto('btc')}
                      style={{
                        padding: '15px',
                        background: '#f3f4f6',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '600',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#e5e7eb';
                        e.currentTarget.style.borderColor = '#16a34a';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#f3f4f6';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }}
                    >
                      <span style={{ fontSize: '20px' }}>₿</span>
                      <span>Bitcoin (BTC)</span>
                    </button>
                    <button
                      onClick={() => setSelectedCrypto('eth')}
                      style={{
                        padding: '15px',
                        background: '#f3f4f6',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '600',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#e5e7eb';
                        e.currentTarget.style.borderColor = '#16a34a';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#f3f4f6';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }}
                    >
                      <span style={{ fontSize: '20px' }}>Ξ</span>
                      <span>Ethereum (ETH)</span>
                    </button>
                    <button
                      onClick={() => setSelectedCrypto('ltc')}
                      style={{
                        padding: '15px',
                        background: '#f3f4f6',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '600',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#e5e7eb';
                        e.currentTarget.style.borderColor = '#16a34a';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#f3f4f6';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }}
                    >
                      <span style={{ fontSize: '20px' }}>Ł</span>
                      <span>Litecoin (LTC)</span>
                    </button>
                    <button
                      onClick={() => setSelectedCrypto('usdterc20')}
                      style={{
                        padding: '15px',
                        background: '#f3f4f6',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '600',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#e5e7eb';
                        e.currentTarget.style.borderColor = '#16a34a';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#f3f4f6';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }}
                    >
                      <span style={{ fontSize: '20px' }}>₮</span>
                      <span>USDT (ERC20)</span>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ background: '#d1fae5', border: '1px solid #10b981', borderRadius: '8px', padding: '15px', marginBottom: '15px' }}>
                    <p style={{ fontSize: '14px', color: '#065f46', marginBottom: '5px' }}><strong>Montant:</strong> {selectedAmount}€</p>
                    <p style={{ fontSize: '14px', color: '#065f46' }}><strong>Méthode:</strong> {
                      selectedCrypto === 'btc' ? 'Bitcoin (BTC)' :
                      selectedCrypto === 'eth' ? 'Ethereum (ETH)' :
                      selectedCrypto === 'ltc' ? 'Litecoin (LTC)' :
                      'USDT (ERC20)'
                    }</p>
                  </div>
                  <button
                    onClick={() => setSelectedCrypto(null)}
                    style={{
                      padding: '8px 16px',
                      background: '#e5e7eb',
                      color: '#6b7280',
                      borderRadius: '6px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      marginBottom: '15px'
                    }}
                  >
                    ← Changer la méthode
                  </button>
                </div>
              </>
            )}

            <div style={{ background: '#f3f4f6', borderRadius: '8px', padding: '15px', marginBottom: '20px' }}>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}><strong>💡 Info:</strong></p>
              <p style={{ fontSize: '14px', color: '#6b7280' }}>
                1€ = 1 crédit = 4 soumissions de formulaire
              </p>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              {selectedAmount && selectedCrypto && (
                <button
                  onClick={handleRecharge}
                  disabled={processingPayment}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: processingPayment ? '#9ca3af' : '#16a34a',
                    color: 'white',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: processingPayment ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    opacity: processingPayment ? 0.6 : 1
                  }}
                >
                  {processingPayment ? 'Traitement...' : 'Procéder au paiement'}
                </button>
              )}
              <button
                onClick={() => {
                  setShowRechargeModal(false);
                  setSelectedAmount(null);
                  setCustomAmount('');
                  setSelectedCrypto(null);
                }}
                style={{
                  padding: '12px 24px',
                  background: '#e5e7eb',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                {selectedAmount || selectedCrypto ? 'Annuler' : 'Fermer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Historique des Paiements */}
      {showPaymentsModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 50 }}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '30px', width: '100%', maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold' }}>Historique des paiements</h2>
              <button
                onClick={() => {
                  setShowPaymentsModal(false);
                  setPayments([]);
                }}
                style={{
                  padding: '8px 16px',
                  background: '#e5e7eb',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Fermer
              </button>
            </div>

            {payments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <p>Aucun paiement pour le moment</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {payments.map((payment) => {
                  const getStatusColor = (status) => {
                    switch (status) {
                      case 'finished':
                      case 'confirmed':
                        return { bg: '#d1fae5', color: '#065f46', text: 'Confirmé' };
                      case 'waiting':
                      case 'confirming':
                        return { bg: '#fef3c7', color: '#92400e', text: 'En attente' };
                      case 'sending':
                      case 'partially_paid':
                        return { bg: '#dbeafe', color: '#1e40af', text: 'En cours' };
                      case 'failed':
                      case 'expired':
                        return { bg: '#fee2e2', color: '#991b1b', text: 'Échoué' };
                      case 'refunded':
                        return { bg: '#e0e7ff', color: '#4338ca', text: 'Remboursé' };
                      default:
                        return { bg: '#f3f4f6', color: '#6b7280', text: status };
                    }
                  };

                  const statusStyle = getStatusColor(payment.status);
                  const cryptoName = payment.pay_currency === 'btc' ? 'Bitcoin' :
                                   payment.pay_currency === 'eth' ? 'Ethereum' :
                                   payment.pay_currency === 'ltc' ? 'Litecoin' :
                                   payment.pay_currency === 'usdterc20' ? 'USDT (ERC20)' :
                                   payment.pay_currency || 'N/A';

                  return (
                    <div
                      key={payment.id}
                      style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '15px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            background: statusStyle.bg,
                            color: statusStyle.color
                          }}>
                            {statusStyle.text}
                          </span>
                          <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
                            {payment.amount}€
                          </span>
                        </div>
                        <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
                          <strong>Méthode:</strong> {cryptoName}
                        </div>
                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                          {new Date(payment.created_at).toLocaleString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                        {payment.payment_id && (
                          <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                            ID: {payment.payment_id}
                          </div>
                        )}
                      </div>
                      {payment.payment_url && (payment.status === 'waiting' || payment.status === 'confirming') && (
                        <button
                          onClick={() => window.open(payment.payment_url, '_blank')}
                          style={{
                            padding: '8px 16px',
                            background: '#2563eb',
                            color: 'white',
                            borderRadius: '6px',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}
                        >
                          Payer
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;